
import type React from 'react';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGym } from '@/contexts/gym-context';
import { useUsers } from '@/api/users';
import { usePlans } from '@/api/plans';
import { useAddPayment } from '@/api/payments';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { InstallmentInput, type Installment } from './installment-input';
import { Combobox } from '@/components/ui/combobox';
import { User } from 'lucide-react';

interface AddPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId?: string; // Optional userId to preselect and lock user
}

// Helper function to format currency based on currency code
const formatCurrency = (amount: number | string, currency = 'ALL') => {
  const numAmount =
    typeof amount === 'string' ? Number.parseFloat(amount) : amount;

  switch (currency) {
    case 'USD':
      return `$${numAmount.toFixed(2)}`;
    case 'EUR':
      return `€${numAmount.toFixed(2)}`;
    case 'GBP':
      return `£${numAmount.toFixed(2)}`;
    case 'ALL':
    default:
      return `${numAmount.toFixed(2)} L`;
  }
};

export function AddPaymentDialog({
  open,
  onOpenChange,
  userId,
}: AddPaymentDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { selectedGym } = useGym();
  const {
    data: users,
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useUsers(selectedGym || '');
  const {
    data: plans,
    isLoading: plansLoading,
    refetch: refetchPlans,
  } = usePlans(selectedGym || '');
  const addPaymentMutation = useAddPayment();

  // Add translations if they don't exist
  const translations = {
    useInstallments: t('payments.useInstallments', 'Use Installments'),
    enableInstallments: t(
      'payments.enableInstallments',
      'Enable payment in installments'
    ),
    installments: t('payments.installments', 'Installments'),
    atLeastOneInstallment: t(
      'payments.atLeastOneInstallment',
      'At least one installment is required'
    ),
    allInstallmentAmounts: t(
      'payments.allInstallmentAmounts',
      'All installment amounts must be valid'
    ),
    paymentDetails: t('payments.paymentDetails', 'Payment Details'),
  };

  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(userId || '');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<string>('Cash');
  const [paymentStatus, setPaymentStatus] = useState<string>('paid');
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [useInstallments, setUseInstallments] = useState(false);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [isOffer, setIsOffer] = useState(false);

  // Handle payment status change
  const handlePaymentStatusChange = (value: string) => {
    setPaymentStatus(value);

    // If status is "free", set amount to 0 and clear installments
    if (value === 'free') {
      setAmount('0');
      setInstallments([]);
      setUseInstallments(false);
    }
  };

  // Debug logging for users
  useEffect(() => {
    console.log('Users data:', users);
    if (users && users.length > 0) {
      console.log('First user:', users[0]);
      console.log(
        'User ID property:',
        users[0].id ? 'id' : users[0]._id ? '_id' : 'unknown'
      );
    }
  }, [users]);

  // Debug logging for selected user
  useEffect(() => {
    console.log('Selected User ID:', selectedUser);
  }, [selectedUser]);

  // Add this after the other useEffect hooks
  useEffect(() => {
    if (open && selectedGym) {
      console.log('Dialog opened, refetching plans and users...');
      refetchPlans();
      refetchUsers();
    }
  }, [open, selectedGym, refetchPlans, refetchUsers]);

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      setSelectedUser('');
      setSelectedPlan('');
      setAmount('');
      setPaymentMethod('Cash');
      setPaymentStatus('paid');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setFormErrors({});
      setUseInstallments(false);
      setInstallments([]);
      setIsOffer(false);
      setUserSearch('');
      setPlanSearch('');
    } else if (userId) {
      setSelectedUser(userId);
    }
  }, [open, userId]);

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    console.log('Validating form with userId:', selectedUser);

    if (!selectedUser) {
      errors.userId = 'User is required';
    }

    if (!selectedPlan) {
      errors.planId = 'Plan is required';
    }

    if (!useInstallments) {
      // For free payments, allow amount to be 0
      if (paymentStatus === 'free') {
        if (!amount || isNaN(Number(amount)) || Number(amount) < 0) {
          errors.amount = 'Valid amount is required';
        }
      } else {
        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
          errors.amount = 'Valid amount is required';
        }
      }
    } else {
      if (installments.length === 0) {
        errors.installments = translations.atLeastOneInstallment;
      } else {
        const invalidInstallments = installments.some(
          (inst) =>
            !inst.amount ||
            isNaN(Number(inst.amount)) ||
            Number(inst.amount) <= 0
        );
        if (invalidInstallments) {
          errors.installments = translations.allInstallmentAmounts;
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    console.log('Form submitted with userId:', selectedUser);

    if (!validateForm()) {
      toast({
        title: t('common.error'),
        description: t('common.pleaseFixErrors'),
        variant: 'destructive',
      });
      return;
    }

    if (!selectedGym) {
      toast({
        title: t('common.error'),
        description: t('common.noActiveGym'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      console.log('Submitting payment with userId:', selectedUser);

      const paymentData: any = {
        userId: selectedUser,
        planId: selectedPlan,
        date: paymentDate,
        method: paymentMethod,
        status: paymentStatus,
        gymId: selectedGym,
        isOffer: isOffer,
      };

      // Get plan price
      const selectedPlanObj = plans?.find(
        (p) => p._id === selectedPlan || (p as { id?: string }).id === selectedPlan
      );
      const planPrice = selectedPlanObj?.price || 0;

      // Set amount based on payment status
      if (paymentStatus === 'free') {
        paymentData.amount = 0;
      } else {
        paymentData.amount = planPrice;
      }

      if (useInstallments) {
        paymentData.installments = installments;
      } else {
        // Create a single installment with the full amount
        paymentData.installments = [
          {
            amount: Number(amount),
            date: paymentDate,
          },
        ];
      }

      await addPaymentMutation.mutateAsync(paymentData);

      toast({
        title: t('common.success'),
        description: t('payments.paymentAdded'),
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      toast({
        title: t('common.error'),
        description: t('common.errorOccurred'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // When a plan is selected, set the amount to the plan's price
  const handlePlanChange = (planId: string) => {
    setSelectedPlan(planId);
    if (plans) {
      const selectedPlan = plans.find((p: any) => p._id === planId);
      if (selectedPlan) {
        setAmount(selectedPlan.price?.toString() || '0');
        if (useInstallments && selectedPlan.price) {
          setInstallments([
            {
              amount: selectedPlan.price,
              date: paymentDate,
            },
          ]);
        }
      }
    }
  };

  // Format users for display
  const formattedUsers =
    users?.map((user: any) => ({
      id: user._id || user.id,
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      phone: user.phone || '',
      avatar: user.avatarUrl || undefined,
    })) || [];

  // User search state for combobox
  const [userSearch, setUserSearch] = useState('');

  // Plan search state for combobox
  const [planSearch, setPlanSearch] = useState('');

  // Filter users based on search
  const filteredUsers =
    userSearch.trim() === ''
      ? formattedUsers
      : formattedUsers.filter(
          (u) =>
            u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.surname.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.phone.toLowerCase().includes(userSearch.toLowerCase())
        );

  // Format plans for display
  const formattedPlans =
    plans?.map((plan: any) => ({
      id: plan._id,
      name: plan.name,
      price: plan.price,
      currency: plan.currency || 'ALL',
      gyms: plan.gyms || [], // Assuming plans have a gyms array
    })) || [];

  const selectedGymPlans = formattedPlans.filter((plan) => {
    return (
      Array.isArray(plan.gyms) && selectedGym && plan.gyms.includes(selectedGym)
    );
  });

  // Filter plans based on search
  const filteredPlans = selectedGymPlans.filter(
    (plan) =>
      planSearch.trim() === '' ||
      plan.name.toLowerCase().includes(planSearch.toLowerCase())
  );

  // Debug user selection
  const handleUserChange = (value: string) => {
    console.log('User selected:', value);
    console.log('User selection event fired');
    setSelectedUser(value);

    // Clear error if present
    if (formErrors.userId) {
      const newErrors = { ...formErrors };
      delete newErrors.userId;
      setFormErrors(newErrors);
    }
  };

  // Handle installments toggle
  const handleInstallmentsToggle = (checked: boolean) => {
    setUseInstallments(checked);

    // If enabling installments and we have an amount, create a default installment
    if (checked && amount) {
      setInstallments([
        {
          amount: Number(amount),
          date: paymentDate,
        },
      ]);
    }
  };

  if (usersLoading || plansLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{t('payments.addPayment')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>{t('payments.addPayment')}</DialogTitle>
          <DialogDescription>
            {t('payments.addPaymentDescription')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 py-4">
            {/* Debug info */}
            <div className="text-xs text-gray-500 mb-2">
              Users loaded: {formattedUsers.length}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* User Combobox */}
              <div className="space-y-2">
                <Label htmlFor="user">{t('payments.user')}</Label>
                <Combobox
                  id="user"
                  value={selectedUser}
                  onValueChange={handleUserChange}
                  options={filteredUsers.map((user) => ({
                    value: user.id,
                    label: user.name + (user.surname ? ` ${user.surname}` : ''),
                    description: user.email || user.phone,
                    avatar: user.avatar,
                  }))}
                  searchValue={userSearch}
                  onSearchValueChange={setUserSearch}
                  placeholder={t('payments.selectUser')}
                  renderOption={(option: any) => (
                    <div className="flex items-center gap-2">
                      {option.avatar ? (
                        <img
                          src={option.avatar}
                          alt="avatar"
                          className="w-6 h-6 rounded-full"
                        />
                      ) : (
                        <User className="w-6 h-6 text-muted-foreground" />
                      )}
                      <div>
                        <div>{option.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                    </div>
                  )}
                  noOptionsMessage={t(
                    'payments.noUsersFound',
                    'No users found'
                  )}
                  className={formErrors.userId ? 'border-red-500' : ''}
                  disabled={!!userId}
                />
                {formErrors.userId && (
                  <p className="text-sm text-red-500 mt-1">
                    {formErrors.userId}
                  </p>
                )}
              </div>

              {/* Plan Combobox */}
              <div className="space-y-2">
                <Label htmlFor="plan">{t('payments.plan')}</Label>
                <Combobox
                  id="plan"
                  value={selectedPlan}
                  onValueChange={handlePlanChange}
                  options={filteredPlans.map((plan) => ({
                    value: plan.id,
                    label: plan.name,
                    description: formatCurrency(plan.price, plan.currency),
                  }))}
                  searchValue={planSearch}
                  onSearchValueChange={setPlanSearch}
                  placeholder={t('payments.selectPlan')}
                  renderOption={(option) => (
                    <div className="flex items-center justify-between w-full">
                      <div>{option.label}</div>
                      <div className="text-sm font-medium">
                        {option.description}
                      </div>
                    </div>
                  )}
                  noOptionsMessage={t(
                    'payments.noPlansFound',
                    'No plans found'
                  )}
                  className={formErrors.planId ? 'border-red-500' : ''}
                />
                {formErrors.planId && (
                  <p className="text-sm text-red-500 mt-1">
                    {formErrors.planId}
                  </p>
                )}
              </div>
            </div>

            {/* Payment Details Section */}
            <div>
              <h3 className="text-base font-medium mb-3">
                {translations.paymentDetails}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Installments Toggle */}
                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox
                    id="useInstallments"
                    checked={useInstallments}
                    disabled={paymentStatus === 'free'}
                    onCheckedChange={handleInstallmentsToggle}
                  />
                  <Label htmlFor="useInstallments" className="font-normal">
                    {translations.enableInstallments}
                  </Label>
                </div>

                {/* Is Offer Toggle */}
                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox
                    id="isOffer"
                    checked={isOffer}
                    onCheckedChange={(checked) => setIsOffer(checked === true)}
                  />
                  <Label htmlFor="isOffer" className="font-normal">
                    {t('payments.isOffer', 'Is Offer')}
                  </Label>
                </div>

                {/* Single Amount (when not using installments) */}
                {!useInstallments && (
                  <div className="space-y-2">
                    <Label htmlFor="amount">{t('common.amount')}</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      className={formErrors.amount ? 'border-red-500' : ''}
                      value={amount}
                      disabled={paymentStatus === 'free'}
                      onChange={(e) => {
                        setAmount(e.target.value);
                        if (formErrors.amount) {
                          const newErrors = { ...formErrors };
                          delete newErrors.amount;
                          setFormErrors(newErrors);
                        }
                      }}
                    />
                    {paymentStatus === 'free' && (
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'payments.freePaymentNote',
                          'Amount is automatically set to 0 for free payments'
                        )}
                      </p>
                    )}
                    {formErrors.amount && (
                      <p className="text-sm text-red-500 mt-1">
                        {formErrors.amount}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="method">{t('common.method')}</Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={(value: string) => setPaymentMethod(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">{t('payments.cash')}</SelectItem>
                      <SelectItem value="Credit Card" disabled>
                        {t('payments.creditCard')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date">{t('payments.date')}</Label>
                  <Input
                    id="date"
                    type="date"
                    required
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">{t('common.status')}</Label>
                  <Select
                    value={paymentStatus}
                    onValueChange={handlePaymentStatusChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">{t('payments.paid')}</SelectItem>
                      <SelectItem value="partially_paid">
                        {t('payments.partiallyPaid')}
                      </SelectItem>
                      <SelectItem value="free">{t('payments.free')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Installments (when using installments) */}
            {useInstallments && (
              <div>
                <InstallmentInput
                  value={installments}
                  onChange={setInstallments}
                  suggestedAmount={Number(amount)}
                />
                {formErrors.installments && (
                  <p className="text-sm text-red-500 mt-1">
                    {formErrors.installments}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
